import { get, filter } from "lodash";
import { Formik, Form, ErrorMessage } from "formik";
import * as yup from "yup";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { useLocation } from "react-router-dom";
import { ProgressSpinner } from "primereact/progressspinner";
import { Title } from "components/Title";
import useApp from "hooks/useApp";
import useAddPage from "hooks/useAddPage";
import { InputMask } from "primereact/inputmask";
import { Calendar } from "primereact/calendar";
import moment from "moment/moment";
import { useEffect, useMemo, useState } from "react";
import {
  RequestBody,
  ingestionBody as IngestionData,
} from "pages/api/Ingestion";
import { useTranslation } from "react-i18next";

function isValidJSON(str) {
  try {
    JSON.parse(str);
    return true;
  } catch (error) {
    return false;
  }
}

const updateOrInsert = (arr, obj, key) => {
  const index = arr.findIndex((item) => item[key] === obj[key]);
  if (index !== -1) {
    // Update existing object
    arr[index] = { ...arr[index], ...obj };
  } else {
    // Insert new object
    arr.push(obj);
  }
  return arr;
};

export const getActionsFlow = (actions, modules, module_id) => {
  const moduleData =
    modules.find((module) => module.id === Number(module_id)) || {};
  const moduleConfig = isValidJSON(moduleData.module_config)
    ? JSON.parse(moduleData.module_config)
    : {};
  const journeyActionsflow = moduleConfig.journeyActionsflow || [];
  return journeyActionsflow.map((step) => {
    const { id, action_name } =
      actions.find(
        (action) =>
          action.action_name === step.key &&
          action.module_id === Number(module_id)
      ) || {};
    return {
      action_name,
      id,
    };
  });
};

const EventsAddPage = (props) => {
  const app = useApp();
  const location = useLocation();
  const { state } = location;
  const { t } = useTranslation();
  const FACILITIES = t("FACILITIES");
  const SELECT_FACILITY = t("SELECT_FACILITY");
  const MODULE = t("MODULE");
  const SELECT_MODULE = t("SELECT_MODULE");
  const EVENT_TIME = t("EVENT_TIME");
  const SELECT_EVENT_TIME = t("SELECT_EVENT_TIME");
  const PLEASE_SELECT_FACILITY_FIRST = t("PLEASE_SELECT_FACILITY_FIRST");
  const SELECT_ACTION = t("SELECT_ACTION");
  const PLEASE_SELECT_MODULE_FIRST = t("PLEASE_SELECT_MODULE_FIRST");
  const MRN = t("MRN");
  const ENTER_MRN = t("ENTER_MRN");
  const PERSONNEL_NAME = t("PERSONNEL_NAME");
  const ENTER_PERSONNEL_NAME = t("ENTER_PERSONNEL_NAME");
  const DOCTORS_NAME = t("DOCTORS_NAME");
  const ENTER_DOCTORS_NAME = t("ENTER_DOCTORS_NAME");
  const EVENT_CHANNEL = t("EVENT_CHANNEL");
  const ENTER_EVENT_CHANNEL = t("ENTER_EVENT_CHANNEL");
  const EVENT_DETAILS = t("EVENT_DETAILS");
  const ENTER_EVENT_DETAILS = t("ENTER_EVENT_DETAILS");
  const QUEUE_NUMBER = t("QUEUE_NUMBER");
  const ENTER_QUEUE_NUMBER = t("ENTER_QUEUE_NUMBER");
  const ENCOUNTER_NUMBER = t("ENCOUNTER_NUMBER");
  const ENTER_ENCOUNTER_NUMBER = t("ENTER_ENCOUNTER_NUMBER");
  const EVENT_LOCATION = t("EVENT_LOCATION");
  const ENTER_EVENT_LOCATION = t("ENTER_EVENT_LOCATION");
  const PERSONNEL_GENDER = t("PERSONNEL_GENDER");
  const ENTER_PERSONNEL_GENDER = t("ENTER_PERSONNEL_GENDER");
  const PERSONNEL_CONTACT = t("PERSONNEL_CONTACT");
  const ENTER_PERSONNEL_CONTACT = t("ENTER_PERSONNEL_CONTACT");
  const PERSONNEL_ROLE = t("PERSONNEL_ROLE");
  const ENTER_PERSONNEL_ROLE = t("ENTER_PERSONNEL_ROLE");
  const PERSONNEL_DETAILS = t("PERSONNEL_DETAILS");
  const ENTER_PERSONNEL_DETAILS = t("ENTER_PERSONNEL_DETAILS");
  const PERSONNEL_TAGS = t("PERSONNEL_TAGS");
  const ENTER_PERSONNEL_TAGS = t("ENTER_PERSONNEL_TAGS");
  const DURATION = t("DURATION");
  const ENTER_DURATION = t("ENTER_DURATION");
  const INVESTIGATION = t("INVESTIGATION");
  const ENTER_INVESTIGATION = t("ENTER_INVESTIGATION");
  const PATIENT_TYPE = t("PATIENT_TYPE");
  const SUBMIT = t("SUBMIT");
  const RESET = t("RESET");
  const ACTIONS = t("ACTIONS");
  const SELECT_PATIENT_TYPE = t("SELECT_PATIENT_TYPE");
  const CREATE_EVENT = t("CREATE_EVENT");

  //form validation rules
  const validationSchema = yup.object().shape({
    org_code: yup.string().required(t("FACILITY_REQUIRED")),
    module_id: yup.number().required(t("MODULE_REQUIRED")),
    action_id: yup.number().required(t("ACTIONS_REQUIRED")),
    channel: yup.string().nullable().label("Channel"),
    mrn: yup.string().required(t("MRN_REQUIRED")),
    dr_name: yup.string().required(t("DOCTORS_REQUIRED")),
    queue_number: yup.string().nullable().label("Queue Number"),
    encounter_number: yup.string().nullable().label("Encounter Number"),
    location: yup.string().nullable().label("Location"),
    personnel_name: yup.string().required(t("PERSONNELNAME_REQUIRED")),
    personnel_contact: yup.string().nullable().label("Personnel Contact"),
    personnel_role: yup.string().nullable().label("Personnel Role"),
    personnel_details: yup.string().nullable().label("Personnel Details"),
    personnel_tags: yup.string().nullable().label("Personnel Tags"),
    event_time: yup.date().required(t("EVENTTIME_REQUIRED")),
    duration: yup.string().nullable().label("Duration"),
    investigation: yup.string().nullable().label("Investigation"),
    patient_type: yup.string().nullable().label("Patient Type"),
    patient_referrals: yup.string().nullable().label("Patient Referrals"),
  });

  const [showCURL, setShowCURL] = useState(false);
  const [ingestionBody, setIngestionBody] = useState(
    IngestionData.map((d) => ({ ...d, sampleVal: "" }))
  );
  const [copyIconClass3, setCopyIconClass3] = useState("pi pi-clone");

  const ingestionUrl = useMemo(() => {
    return "https://demo.hm.vkraftsoftware.com/api/es/ingest/event";
    // return process.env.REACT_APP_API_PATH ?? "/api/" + "es/ingest/event";
  }, [process.env.REACT_APP_API_PATH]);

  // Form default values
  const defaultValues = {
    org_code: "",
    module_id: "",
    action_id: "",
    channel: "",
    details: "",
    mrn: "",
    dr_name: "",
    queue_number: "",
    encounter_number: "",
    location: "",
    personnel_name: "",
    personnel_contact: "",
    personnel_role: "",
    personnel_details: "",
    personnel_tags: "",
    event_time: "",
    duration: "",
    investigation: "",
    patient_type: "",
    personnel_gender: "",
    patient_referrals:"",
  };
  const formDefaultValues =
    state && state.patientData ? state.patientData : defaultValues;

  //page hook where logics resides
  const pageController = useAddPage({ props, formDefaultValues, afterSubmit });

  // destructure and grab what the page needs
  const {
    formData,
    resetForm,
    handleSubmit,
    submitForm,
    pageReady,
    loading,
    saving,
    inputClassName,
  } = pageController;

  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  //event raised after form submit
  function afterSubmit(response) {
    app.flashMsg(props.msgTitle, props.msgAfterSave);
    resetForm(defaultValues);
    if (app.isDialogOpen()) {
      app.closeDialogs(); // if page is open as dialog, close dialog
    } else if (props.redirect) {
      app.navigate(
        state ? `/dashboard/${state.patientData.module_id}?legend=0` : "/events"
      );
    }
  }
  const handleReset = (formik) => {
    formik.resetForm({
      values: {
        ...defaultValues,
      },
    });
  };
  const getInputClassName = (error, touched) => {
    return error && touched ? "p-invalid" : "";
  };
  // page loading form data from api
  if (loading) {
    return (
      <div className="p-3 text-center">
        <ProgressSpinner style={{ width: "50px", height: "50px" }} />
      </div>
    );
  }

  const parseEventTime = (eventTime) => {
    return eventTime ? moment(eventTime).toDate() : null;
  };

  const onChangeOfFields = (name, value) => {
    const updatedArry = updateOrInsert(
      JSON.parse(JSON.stringify(ingestionBody)),
      { label: name, sampleVal: value },
      "label"
    );
    setIngestionBody(updatedArry);
  };
  //page has loaded any required data and ready to render
  if (pageReady) {
    return (
      <main id="EventsAddPage" className="main-page">
        {props.showHeader && (
          <section className="page-section mb-3">
            <div className="container">
              <div className="grid align-items-center">
                {!props.isSubPage && (
                  <div className="col-fixed ">
                    <Button
                      onClick={() => app.navigate(-1)}
                      label=""
                      className="p-button p-button-text "
                      icon="pi pi-arrow-left"
                    />
                  </div>
                )}
                <div className="col ">
                  <Title
                    title={CREATE_EVENT}
                    titleClass="text-2xl text-primary font-bold"
                    subTitleClass="text-500"
                    separator={false}
                  />
                </div>
              </div>
            </div>
          </section>
        )}
        <section className="page-section ">
          <div className="container">
            <div className="grid ">
              <div className="md:col-7 sm:col-12 comp-grid">
                <div>
                  <Formik
                    initialValues={formData}
                    validationSchema={validationSchema}
                    onSubmit={(values, actions) => submitForm(values)}
                  >
                    {(formik) => {
                      return (
                        <>
                          <Form
                            className={`${!props.isSubPage ? "card  " : ""}`}
                          >
                            <div className="grid">
                              <div className="col-12">
                                <div className="formgrid grid">
                                  <div className="col-12 md:col-3">
                                    {EVENT_TIME} *
                                  </div>
                                  <div className="col-12 md:col-9">
                                    <Calendar
                                      name="event_time"
                                      value={parseEventTime(
                                        formik.values.event_time
                                      )}
                                      onChange={(e) => {
                                        const dateValue = moment(
                                          e.value
                                        ).isValid()
                                          ? moment(e.value).format()
                                          : "";
                                        formik.setFieldValue(
                                          "event_time",
                                          dateValue
                                        );
                                        onChangeOfFields(
                                          "event_time",
                                          dateValue
                                        );
                                      }}
                                      dateFormat="yy-mm-dd"
                                      timeFormat="HH:mm:ss"
                                      showTime
                                      hourFormat="24"
                                      showSeconds
                                      hideOnDateTimeSelect
                                      className={`${getInputClassName(
                                        formik.errors.event_time,
                                        formik.touched.event_time
                                      )} w-full`}
                                      appendTo="self"
                                      placeholder={SELECT_EVENT_TIME}
                                    />
                                    <ErrorMessage
                                      name="event_time"
                                      component="span"
                                      className="p-error"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-12">
                                <div className="formgrid grid">
                                  <div className="col-12 md:col-3">
                                    {FACILITIES} *
                                  </div>
                                  <div className="col-12 md:col-9">
                                    <Dropdown
                                      name="org_code"
                                      value={formik.values.org_code || null}
                                      onChange={(e) => {
                                        const newValue =
                                          e.value === undefined ? "" : e.value;
                                        formik.setFieldValue(
                                          "org_code",
                                          newValue
                                        );
                                        formik.setFieldValue("module_id", "");
                                        formik.setFieldValue("action_id", "");
                                        formik.setFieldTouched(
                                          "org_code",
                                          false
                                        );
                                        onChangeOfFields(
                                          "organisation_code",
                                          newValue
                                        );
                                      }}
                                      options={get(props, "organisations", [])}
                                      placeholder={SELECT_FACILITY}
                                      optionLabel="name"
                                      optionValue="code"
                                      className={`${getInputClassName(
                                        formik.errors.org_code,
                                        formik.touched.org_code
                                      )} w-full`}
                                      {...(formik.values.org_code && {
                                        showClear: true,
                                      })}
                                      filter
                                      emptyFilterMessage={t("NO_OPTIONS_FOUND")}
                                    />
                                    <ErrorMessage
                                      name="org_code"
                                      component="span"
                                      className="p-error"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-12">
                                <div className="formgrid grid">
                                  <div className="col-12 md:col-3">
                                    {MODULE} *
                                  </div>
                                  <div className="col-12 md:col-9">
                                    <Dropdown
                                      name="module_id"
                                      value={formik.values.module_id}
                                      onChange={(e) => {
                                        const newValue =
                                          e.value === undefined ? "" : e.value;
                                        formik.setFieldValue(
                                          "module_id",
                                          newValue
                                        );
                                        
                                        
                                        formik.setFieldValue("action_id", "");
                                        formik.setFieldTouched(
                                          "module_id",
                                          false
                                        );
                                        onChangeOfFields(
                                          "module_code",
                                          newValue
                                        );
                                      }}
                                      options={filter(
                                        get(props, "modules", []),
                                        (module) =>
                                          module.org_code ===
                                          formik.values.org_code
                                      )}
                                      optionLabel="module_name"
                                      optionValue="id"
                                      placeholder={
                                        formik.values.org_code
                                          ? SELECT_MODULE
                                          : PLEASE_SELECT_FACILITY_FIRST
                                      }
                                      className={`${getInputClassName(
                                        formik.errors.module_id,
                                        formik.touched.module_id
                                      )} w-full`}
                                      filter
                                      emptyFilterMessage={t("NO_OPTIONS_FOUND")}
                                      emptyMessage={t("NO_RECORDS_FOUND")}
                                      {...(formik.values.module_id && {
                                        showClear: true,
                                      })}
                                    />
                                    <ErrorMessage
                                      name="module_id"
                                      component="span"
                                      className="p-error"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-12">
                                <div className="formgrid grid">
                                  <div className="col-12 md:col-3">
                                    {ACTIONS} *
                                  </div>
                                  <div className="col-12 md:col-9">
                                    <Dropdown
                                      name="action_id"
                                      value={formik.values.action_id}
                                      emptyFilterMessage={t("NO_OPTIONS_FOUND")}
                                      emptyMessage={t("NO_RECORDS_FOUND")}
                                      onChange={(e) => {
                                        const newValue =
                                          e.value === undefined ? "" : e.value;
                                        formik.setFieldValue(
                                          "action_id",
                                          newValue
                                        );
                                        formik.setFieldTouched(
                                          "action_id",
                                          false
                                        );
                                        onChangeOfFields("action", newValue);
                                      }}
                                      options={getActionsFlow(
                                        get(props, "actions", []),
                                        get(props, "modules", []),
                                        formik.values.module_id
                                      )}
                                      optionLabel="action_name"
                                      optionValue="id"
                                      placeholder={
                                        formik.values.module_id
                                          ? SELECT_ACTION
                                          : PLEASE_SELECT_MODULE_FIRST
                                      }
                                      className={getInputClassName(
                                        formik?.errors?.action_id,
                                        formik.touched.action_id
                                      )}
                                      style={{ width: "100%" }}
                                      filter
                                      {...(formik.values.action_id && {
                                        showClear: true,
                                      })}
                                    />
                                    <ErrorMessage
                                      name="action_id"
                                      component="span"
                                      className="p-error"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-12">
                                <div className="formgrid grid">
                                  <div className="col-12 md:col-3">{MRN} *</div>
                                  <div className="col-12 md:col-9">
                                    <InputText
                                      type="text"
                                      name="mrn"
                                      placeholder={ENTER_MRN}
                                      onChange={(e) => {
                                        formik.handleChange(e);
                                        onChangeOfFields("mrn", e.target.value);
                                      }}
                                      value={formik.values.mrn}
                                      className={`${getInputClassName(
                                        formik.errors.mrn,
                                        formik.touched.mrn
                                      )} w-full`}
                                      {...(formik.values.mrn && {
                                        showClear: true,
                                      })}
                                      filter
                                    />
                                    <ErrorMessage
                                      name="mrn"
                                      component="span"
                                      className="p-error"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-12">
                                <div className="formgrid grid">
                                  <div className="col-12 md:col-3">
                                    {PERSONNEL_NAME} *
                                  </div>
                                  <div className="col-12 md:col-9">
                                    <InputText
                                      type="text"
                                      name="personnel_name"
                                      placeholder={ENTER_PERSONNEL_NAME}
                                      onChange={(e) => {
                                        formik.handleChange(e);
                                        onChangeOfFields(
                                          "personnel_name",
                                          e.target.value
                                        );
                                      }}
                                      value={formik.values.personnel_name}
                                      className={`${getInputClassName(
                                        formik.errors.personnel_name,
                                        formik.touched.personnel_name
                                      )} w-full`}
                                      {...(formik.values.personnel_name && {
                                        showClear: true,
                                      })}
                                      filter
                                    />
                                    <ErrorMessage
                                      name="personnel_name"
                                      component="span"
                                      className="p-error"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-12">
                                <div className="formgrid grid">
                                  <div className="col-12 md:col-3">
                                    {DOCTORS_NAME} *
                                  </div>
                                  <div className="col-12 md:col-9">
                                    <InputText
                                      type="text"
                                      name="dr_name"
                                      placeholder={ENTER_DOCTORS_NAME}
                                      onChange={(e) => {
                                        formik.handleChange(e);
                                        onChangeOfFields(
                                          "dr_name",
                                          e.target.value
                                        );
                                      }}
                                      value={formik.values.dr_name}
                                      className={`${getInputClassName(
                                        formik.errors.dr_name,
                                        formik.touched.dr_name
                                      )} w-full`}
                                      {...(formik.values.dr_name && {
                                        showClear: true,
                                      })}
                                      filter
                                    />
                                    <ErrorMessage
                                      name="dr_name"
                                      component="span"
                                      className="p-error"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-12">
                                <div className="formgrid grid">
                                  <div className="col-12 md:col-3">
                                    {EVENT_CHANNEL}
                                  </div>
                                  <div className="col-12 md:col-9">
                                    <InputText
                                      type="text"
                                      name="channel"
                                      placeholder={ENTER_EVENT_CHANNEL}
                                      onChange={(e) => {
                                        formik.handleChange(e);
                                        onChangeOfFields(
                                          "channel",
                                          e.target.value
                                        );
                                      }}
                                      value={formik.values.channel}
                                      className={inputClassName(
                                        formik?.errors?.channel
                                      )}
                                    />
                                    <ErrorMessage
                                      name="channel"
                                      component="span"
                                      className="p-error"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-12">
                                <div className="formgrid grid">
                                  <div className="col-12 md:col-3">
                                    {EVENT_DETAILS}
                                  </div>
                                  <div className="col-12 md:col-9">
                                    <InputText
                                      type="text"
                                      name="details"
                                      placeholder={ENTER_EVENT_DETAILS}
                                      onChange={(e) => {
                                        formik.handleChange(e);
                                        onChangeOfFields(
                                          "details",
                                          e.target.value
                                        );
                                      }}
                                      value={
                                        typeof formik.values.details ===
                                        "object"
                                          ? JSON.stringify(
                                              formik.values.details
                                            )
                                          : formik.values.details
                                      }
                                      className={inputClassName(
                                        formik?.errors?.details
                                      )}
                                    />
                                    <ErrorMessage
                                      name="details"
                                      component="span"
                                      className="p-error"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-12">
                                <div className="formgrid grid">
                                  <div className="col-12 md:col-3">
                                    {QUEUE_NUMBER}
                                  </div>
                                  <div className="col-12 md:col-9">
                                    <InputText
                                      type="text"
                                      name="queue_number"
                                      placeholder={ENTER_QUEUE_NUMBER}
                                      onChange={(e) => {
                                        formik.handleChange(e);
                                        onChangeOfFields(
                                          "queue_number",
                                          e.target.value
                                        );
                                      }}
                                      value={formik.values.queue_number}
                                      className={inputClassName(
                                        formik?.errors?.queue_number
                                      )}
                                    />
                                    <ErrorMessage
                                      name="queue_number"
                                      component="span"
                                      className="p-error"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-12">
                                <div className="formgrid grid">
                                  <div className="col-12 md:col-3">
                                    {ENCOUNTER_NUMBER}
                                  </div>
                                  <div className="col-12 md:col-9">
                                    <InputText
                                      type="text"
                                      name="encounter_number"
                                      placeholder={ENTER_ENCOUNTER_NUMBER}
                                      onChange={(e) => {
                                        formik.handleChange(e);
                                        onChangeOfFields(
                                          "encounter_number",
                                          e.target.value
                                        );
                                      }}
                                      value={formik.values.encounter_number}
                                      className={inputClassName(
                                        formik?.errors?.encounter_number
                                      )}
                                    />
                                    <ErrorMessage
                                      name="encounter_number"
                                      component="span"
                                      className="p-error"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-12">
                                <div className="formgrid grid">
                                  <div className="col-12 md:col-3">
                                    {EVENT_LOCATION}
                                  </div>
                                  <div className="col-12 md:col-9">
                                    <InputText
                                      type="text"
                                      name="location"
                                      placeholder={ENTER_EVENT_LOCATION}
                                      onChange={(e) => {
                                        formik.handleChange(e);
                                        onChangeOfFields(
                                          "location",
                                          e.target.value
                                        );
                                      }}
                                      value={formik.values.location}
                                      className={inputClassName(
                                        formik?.errors?.location
                                      )}
                                    />
                                    <ErrorMessage
                                      name="location"
                                      component="span"
                                      className="p-error"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-12">
                                <div className="formgrid grid">
                                  <div className="col-12 md:col-3">
                                    {PERSONNEL_GENDER}
                                  </div>
                                  <div className="col-12 md:col-9">
                                    <InputText
                                      type="text"
                                      name="personnel_gender"
                                      placeholder={ENTER_PERSONNEL_GENDER}
                                      onChange={(e) => {
                                        formik.handleChange(e);
                                        onChangeOfFields(
                                          "personnel_gender",
                                          e.target.value
                                        );
                                      }}
                                      value={formik.values.personnel_gender}
                                      className={inputClassName(
                                        formik?.errors?.personnel_gender
                                      )}
                                    />
                                    <ErrorMessage
                                      name="personnel_gender"
                                      component="span"
                                      className="p-error"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-12">
                                <div className="formgrid grid">
                                  <div className="col-12 md:col-3">
                                    {PERSONNEL_CONTACT}
                                  </div>
                                  <div className="col-12 md:col-9">
                                    <InputText
                                      type="text"
                                      name="personnel_contact"
                                      placeholder={ENTER_PERSONNEL_CONTACT}
                                      onChange={(e) => {
                                        formik.handleChange(e);
                                        onChangeOfFields(
                                          "personnel_contact",
                                          e.target.value
                                        );
                                      }}
                                      value={formik.values.personnel_contact}
                                      className={inputClassName(
                                        formik?.errors?.personnel_contact
                                      )}
                                    />
                                    <ErrorMessage
                                      name="personnel_contact"
                                      component="span"
                                      className="p-error"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-12">
                                <div className="formgrid grid">
                                  <div className="col-12 md:col-3">
                                    {PERSONNEL_ROLE}
                                  </div>
                                  <div className="col-12 md:col-9">
                                    <InputText
                                      type="text"
                                      name="personnel_role"
                                      placeholder={ENTER_PERSONNEL_ROLE}
                                      onChange={(e) => {
                                        formik.handleChange(e);
                                        onChangeOfFields(
                                          "personnel_role",
                                          e.target.value
                                        );
                                      }}
                                      value={formik.values.personnel_role}
                                      className={inputClassName(
                                        formik?.errors?.personnel_role
                                      )}
                                    />
                                    <ErrorMessage
                                      name="personnel_role"
                                      component="span"
                                      className="p-error"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-12">
                                <div className="formgrid grid">
                                  <div className="col-12 md:col-3">
                                    {PERSONNEL_DETAILS}
                                  </div>
                                  <div className="col-12 md:col-9">
                                    <InputText
                                      type="text"
                                      name="personnel_details"
                                      placeholder={ENTER_PERSONNEL_DETAILS}
                                      onChange={(e) => {
                                        formik.handleChange(e);
                                        onChangeOfFields(
                                          "personnel_details",
                                          e.target.value
                                        );
                                      }}
                                      value={formik.values.personnel_details}
                                      className={inputClassName(
                                        formik?.errors?.personnel_details
                                      )}
                                    />
                                    <ErrorMessage
                                      name="personnel_details"
                                      component="span"
                                      className="p-error"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-12">
                                <div className="formgrid grid">
                                  <div className="col-12 md:col-3">
                                    {PERSONNEL_TAGS}
                                  </div>
                                  <div className="col-12 md:col-9">
                                    <InputText
                                      type="text"
                                      name="personnel_tags"
                                      placeholder={ENTER_PERSONNEL_TAGS}
                                      onChange={(e) => {
                                        formik.handleChange(e);
                                        onChangeOfFields(
                                          "personnel_tags",
                                          e.target.value
                                        );
                                      }}
                                      value={formik.values.personnel_tags}
                                      className={inputClassName(
                                        formik?.errors?.personnel_tags
                                      )}
                                    />
                                    <ErrorMessage
                                      name="personnel_tags"
                                      component="span"
                                      className="p-error"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-12">
                                <div className="formgrid grid">
                                  <div className="col-12 md:col-3">
                                    {DURATION}
                                  </div>
                                  <div className="col-12 md:col-9">
                                    <InputMask
                                      name="duration"
                                      mask="99:99:99"
                                      value={formik.values.duration}
                                      onChange={(e) => {
                                        formik.handleChange(e);
                                        onChangeOfFields(
                                          "duration",
                                          e.target.value
                                        );
                                      }}
                                      slotChar="00:00:00"
                                      className={inputClassName(
                                        formik?.errors?.duration
                                      )}
                                      placeholder={ENTER_DURATION}
                                    />
                                    <ErrorMessage
                                      name="duration"
                                      component="span"
                                      className="p-error"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-12">
                                <div className="formgrid grid">
                                  <div className="col-12 md:col-3">
                                    {INVESTIGATION}
                                  </div>
                                  <div className="col-12 md:col-9">
                                    <InputText
                                      type="text"
                                      name="investigation"
                                      placeholder={ENTER_INVESTIGATION}
                                      onChange={(e) => {
                                        formik.handleChange(e);
                                        onChangeOfFields(
                                          "investigation",
                                          e.target.value
                                        );
                                      }}
                                      value={formik.values.investigation}
                                      className={inputClassName(
                                        formik?.errors?.investigation
                                      )}
                                    />
                                    <ErrorMessage
                                      name="investigation"
                                      component="span"
                                      className="p-error"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-12">
                                <div className="formgrid grid">
                                  <div className="col-12 md:col-3">
                                    {PATIENT_TYPE}
                                  </div>
                                  <div className="col-12 md:col-9">
                                    <Dropdown
                                      value={formik.values.patient_type}
                                      options={props.patientType.map(
                                        (type) => ({
                                          name: type,
                                          value: type,
                                        })
                                      )}
                                      onChange={(e) => {
                                        formik.setFieldValue(
                                          "patient_type",
                                          e.value
                                        );
                                        onChangeOfFields(
                                          "patient_type",
                                          e.value
                                        );
                                      }}
                                      optionLabel="name"
                                      placeholder={SELECT_PATIENT_TYPE}
                                      className="w-full"
                                      {...(formik.values.patient_type && {
                                        showClear: true,
                                      })}
                                    />
                                    <ErrorMessage
                                      name="patient_type"
                                      component="span"
                                      className="p-error"
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-12">
                                <div className="formgrid grid">
                                  <div className="col-12 md:col-3">
                                    {t("PATIENT_REFERRALS")}
                                  </div>
                                  <div className="col-12 md:col-9">
                                    <InputText
                                      type="text"
                                      name="patient_referrals"
                                      placeholder={t("ENTER_PATIENT_REFERRALS")}
                                      onChange={(e) => {
                                        formik.handleChange(e);
                                        onChangeOfFields(
                                          "patient_referrals",
                                          e.target.value
                                        );
                                      }}
                                      value={formik.values.patient_referrals}
                                      className={inputClassName(
                                        formik?.errors?.patient_referrals
                                      )}
                                    />
                                    <ErrorMessage
                                      name="patient_referrals"
                                      component="span"
                                      className="p-error"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            {props.showFooter && (
                              <div className="text-center my-3">
                                <Button
                                  onClick={(e) => handleSubmit(e, formik)}
                                  className=" mr-2"
                                  type="submit"
                                  label={SUBMIT}
                                  icon="pi pi-send"
                                  loading={saving}
                                />
                                <Button
                                  onClick={() => handleReset(formik)}
                                  className="p-button-secondary mr-2"
                                  type="button"
                                  label={RESET}
                                  icon="pi pi-refresh"
                                />
                              </div>
                            )}
                          </Form>
                        </>
                      );
                    }}
                  </Formik>
                </div>
              </div>
              <div className="md:col-5 sm:col-12 comp-grid">
                <RequestBody
                  setShowCURL={setShowCURL}
                  showCURL={showCURL}
                  copyIconClass3={copyIconClass3}
                  setCopyIconClass3={setCopyIconClass3}
                  ingestionUrl={ingestionUrl}
                  ingestionBody={ingestionBody}
                />
              </div>
            </div>
          </div>
        </section>
      </main>
    );
  }
};

//page props and default values
const withDefaultProps = (Component) => {
  const WrappedComponent = (props) => {
    const { t } = useTranslation();
    const defaultProps = {
      primaryKey: "id",
      pageName: "events",
      apiPath: "events/add_event",
      routeName: "eventsadd",
      submitButtonLabel: "Submit",
      formValidationError: t("FORM_IS_INVALID"),
      formValidationMsg: t("PLEASE_COMPLETE_THE_FORM"),
      msgTitle: t("CREATE_EVENT"),
      msgAfterSave: t("EVENT_CREATED_SUCCESSFULLY"),
      msgBeforeSave: "",
      showHeader: true,
      showFooter: true,
      redirect: true,
      isSubPage: false,
    };
    return <Component {...defaultProps} {...props} />;
  };

  return WrappedComponent;
};
export default withDefaultProps(EventsAddPage);
